import { useEffect, useRef } from 'react'
import { Outlet, RouteObject, RouterProvider, createBrowserRouter, matchPath, useLocation } from 'react-router-dom'
import NotFoundPage from './pages/NotFoundPage'
import { ME_ROUTE_ID, MeLoadError, MeLoading, MeProvider, loadMe, useMe } from './context/MeContext'
import { AppShell } from './components/AppShell'
import { RouteErrorPanel } from './components/RouteErrorPanel'
import { setPendingPath } from './lib/api'
import { clearServerFlash, trackEvent } from './lib/config'
import { useInternalLinkNavigation } from './lib/useInternalLinkNavigation'
import { useScrollRestoration } from './lib/useScrollRestoration'
import { ALL_ROUTES, APP_AREA_ROUTES, PUBLIC_ROUTES, RouteSpec } from './routes'

function getMatchedRoute(pathname: string): RouteSpec | undefined {
  return ALL_ROUTES.find((route) => matchPath(route.path, pathname) !== null)
}

// The mode a controller pins comes from the matched route spec, so one layout route serves
// every signed-in screen and MeProvider and AppShell stay mounted from one section to the next.
// The tracker sits inside AppShell so the super properties are registered before the
// deferred page view fires; a tracker above MeProvider would book the first view while
// /api/me is still loading and nothing is registered yet.
export function AppArea() {
  const location = useLocation()
  useInternalLinkNavigation()
  useScrollRestoration()
  return (
    <MeProvider>
      <AppShell modeOverride={getMatchedRoute(location.pathname)?.mode}>
        <PageViewTracker />
        <NavigationEffects />
        <PageOutlet />
      </AppShell>
    </MeProvider>
  )
}

// The server answers with the data of the professional's current profession, so changing it
// remounts the screen to fetch that data again, even when the URL stays the same.
function PageOutlet() {
  const location = useLocation()
  const professionKeys = useMe().health_centers.map((healthCenter) => healthCenter.profession.key)
  const key = [location.pathname, location.search, ...professionKeys].join(' ')
  return <Outlet key={key} />
}

// Compares against the last URL seen rather than a first-render flag, so StrictMode's
// second effect run in development does not count as a navigation.
function NavigationEffects() {
  const location = useLocation()
  const seenUrl = useRef(location.pathname + location.search)

  useEffect(() => {
    const url = location.pathname + location.search
    if (seenUrl.current === url) return
    seenUrl.current = url
    clearServerFlash()
  }, [location.pathname, location.search])

  return null
}

function PublicArea() {
  return (
    <>
      <PageViewTracker />
      <Outlet />
    </>
  )
}

function toRoutes(specs: readonly RouteSpec[]): RouteObject[] {
  return specs.map(({ path, element, loader, shouldRevalidate }) => ({ path, element, loader, shouldRevalidate }))
}

/**
 * Emits one SPA Page Viewed per route the professional actually lands on, carrying the
 * matched pattern rather than the interpolated path so the event name and its `route`
 * property stay a fixed vocabulary. The emit is deferred by a tick and cancelled on
 * cleanup, so a redirect away from the route books nothing, and so AppShell's effect
 * has registered the super properties by the time the event is sent.
 */
function PageViewTracker() {
  const location = useLocation()

  useEffect(() => {
    const timer = window.setTimeout(() => {
      const matched = getMatchedRoute(location.pathname)
      const healthCenterId = matched ? matchPath(matched.path, location.pathname)?.params.healthCenterId : undefined
      trackEvent('SPA Page Viewed', {
        route: matched?.path ?? 'not_found',
        path: location.pathname,
        mode: matched?.mode ?? new URLSearchParams(location.search).get('mode'),
        health_center_id: healthCenterId ? Number(healthCenterId) : null,
      })
    }, 0)
    return () => window.clearTimeout(timer)
  }, [location.pathname, location.search])

  return null
}

// `me` reloads on every navigation, not only when the root's params change, so the navbar
// switches with the page. The pathless child holds the error element, so a failing screen
// keeps the navbar around it.
export const appRoutes: RouteObject[] = [
  { element: <PublicArea />, children: [...toRoutes(PUBLIC_ROUTES), { path: '*', element: <NotFoundPage /> }] },
  {
    id: ME_ROUTE_ID,
    element: <AppArea />,
    loader: loadMe,
    shouldRevalidate: () => true,
    HydrateFallback: MeLoading,
    errorElement: <MeLoadError />,
    children: [{ errorElement: <RouteErrorPanel />, children: toRoutes(APP_AREA_ROUTES) }],
  },
]

const router = createBrowserRouter(appRoutes, { future: { v7_partialHydration: true } })

router.subscribe(({ navigation }) => {
  setPendingPath(navigation.location ? navigation.location.pathname + navigation.location.search : null)
})

export default function App() {
  return <RouterProvider router={router} future={{ v7_startTransition: true }} />
}
